import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { deleteQRCode, listQRCodes, toggleQRCodeActive } from "../services/api";

/* ---------- Inline icons (same set/style as My QR Codes) ---------- */
const Icon = ({ children, size = 16 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    {children}
  </svg>
);

const SearchIcon = () => (
  <Icon>
    <circle cx="11" cy="11" r="7" />
    <path d="m20 20-3.5-3.5" />
  </Icon>
);
const EditIcon = () => (
  <Icon>
    <path d="M12 20h9" />
    <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z" />
  </Icon>
);
const EyeIcon = () => (
  <Icon>
    <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12Z" />
    <circle cx="12" cy="12" r="3" />
  </Icon>
);
const PowerIcon = () => (
  <Icon>
    <path d="M12 2v10" />
    <path d="M18.4 6.6a9 9 0 1 1-12.8 0" />
  </Icon>
);
const TrashIcon = ({ size = 16 }) => (
  <Icon size={size}>
    <path d="M3 6h18" />
    <path d="M8 6V4h8v2" />
    <path d="M19 6l-1 14H6L5 6" />
  </Icon>
);
const CloseIcon = () => (
  <Icon size={18}>
    <path d="M18 6 6 18" />
    <path d="m6 6 12 12" />
  </Icon>
);
const QrIcon = ({ size = 24 }) => (
  <Icon size={size}>
    <rect x="3" y="3" width="7" height="7" rx="1" />
    <rect x="14" y="3" width="7" height="7" rx="1" />
    <rect x="3" y="14" width="7" height="7" rx="1" />
    <path d="M14 14h3v3h-3zM20 14v0M14 20h3M20 17v4" />
  </Icon>
);
const DownloadIcon = () => (
  <Icon>
    <path d="M12 3v12" />
    <path d="m7 10 5 5 5-5" />
    <path d="M5 21h14" />
  </Icon>
);

/* ---------- Preview modal ---------- */
function PreviewModal({ qr, onClose, onDownload }) {
  useEffect(() => {
    if (!qr) return undefined;
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [qr, onClose]);

  if (!qr) return null;

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div
        className="modal"
        role="dialog"
        aria-modal="true"
        aria-label={`Preview of ${qr.title}`}
        onClick={(e) => e.stopPropagation()}
      >
        <button className="modal-close" onClick={onClose} aria-label="Close preview">
          <CloseIcon />
        </button>
        <img className="modal-qr" src={qr.generated_image} alt={qr.title} />
        <div className="modal-title">{qr.title}</div>
        <div className="modal-url">{qr.destination_url}</div>
        <button className="btn btn-primary" style={{ width: "100%" }} onClick={() => onDownload(qr)}>
          <DownloadIcon /> Download PNG
        </button>
      </div>
    </div>
  );
}

/* ---------- Confirm delete modal ---------- */
function ConfirmDeleteModal({ qr, onClose, onConfirm, deleting }) {
  useEffect(() => {
    if (!qr) return undefined;
    const onKey = (e) => e.key === "Escape" && !deleting && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [qr, onClose, deleting]);

  if (!qr) return null;

  return (
    <div className="modal-backdrop" onClick={() => !deleting && onClose()}>
      <div
        className="modal"
        role="alertdialog"
        aria-modal="true"
        aria-label="Confirm delete"
        onClick={(e) => e.stopPropagation()}
      >
        <button className="modal-close" onClick={onClose} disabled={deleting} aria-label="Close">
          <CloseIcon />
        </button>

        <div
          style={{
            width: 56, height: 56, margin: "4px auto 16px",
            display: "flex", alignItems: "center", justifyContent: "center",
            borderRadius: 16, background: "var(--myg-danger-bg)", color: "var(--myg-danger)",
          }}
        >
          <TrashIcon size={26} />
        </div>

        <div className="modal-title">Delete "{qr.title}"?</div>
        <div className="modal-url" style={{ wordBreak: "normal" }}>
          {qr.owner_username && (
            <>
              Owned by <strong style={{ color: "#111827" }}>{qr.owner_username}</strong>.{" "}
            </>
          )}
          It has been scanned{" "}
          <strong style={{ color: "#111827" }}>
            {qr.scan_count} time{qr.scan_count === 1 ? "" : "s"}
          </strong>
          . This action is permanent and can't be undone.
        </div>

        <div style={{ display: "flex", gap: 10 }}>
          <button
            className="btn btn-secondary"
            style={{ flex: 1 }}
            onClick={onClose}
            disabled={deleting}
          >
            Cancel
          </button>
          <button
            className="btn btn-danger"
            style={{ flex: 1 }}
            onClick={onConfirm}
            disabled={deleting}
          >
            {deleting ? "Deleting..." : "Delete"}
          </button>
        </div>
      </div>
    </div>
  );
}

const fmtDateTime = (value) =>
  value
    ? new Date(value).toLocaleString("en-GB", {
        day: "2-digit", month: "short", year: "numeric"
      })
    : "—";

/* ---------- Page ---------- */
export default function AdminQRCodes() {
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [togglingId, setTogglingId] = useState(null);
  const [previewQr, setPreviewQr] = useState(null);
  const [query, setQuery] = useState("");
  const [confirmQr, setConfirmQr] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const load = () => {
    setLoading(true);
    listQRCodes()
      .then(({ data }) => setCodes(data.results || data))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return codes;
    return codes.filter(
      (c) =>
        (c.title || "").toLowerCase().includes(q) ||
        (c.destination_url || "").toLowerCase().includes(q) ||
        (c.owner_username || "").toLowerCase().includes(q)
    );
  }, [codes, query]);

  const handleConfirmDelete = async () => {
    if (!confirmQr) return;
    setDeleting(true);
    try {
      await deleteQRCode(confirmQr.id);
      setConfirmQr(null);
      load();
    } finally {
      setDeleting(false);
    }
  };

  const handleToggleActive = async (id) => {
    setTogglingId(id);
    try {
      const { data } = await toggleQRCodeActive(id);
      setCodes((prev) => prev.map((c) => (c.id === id ? { ...c, is_active: data.is_active } : c)));
    } finally {
      setTogglingId(null);
    }
  };

  const handleDownload = async (qr) => {
    try {
      const res = await fetch(qr.generated_image);
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${qr.title || "qr-code"}.png`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      window.open(qr.generated_image, "_blank");
    }
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">All QR Codes</div>
          <div className="page-subtitle">Every QR code created by every user, in one place.</div>
        </div>
      </div>

      <div className="card card-flush">
        {loading ? (
          <div style={{ padding: "8px 0" }}>
            <div className="skeleton-row" />
            <div className="skeleton-row" />
            <div className="skeleton-row" />
          </div>
        ) : codes.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">
              <QrIcon size={26} />
            </div>
            <div className="empty-state-title">No QR codes yet</div>
            <div className="empty-state-text">
              Once users start creating QR codes, they'll show up here.
            </div>
          </div>
        ) : (
          <>
            <div className="list-toolbar">
              <div className="search-box">
                <SearchIcon />
                <input
                  className="form-input"
                  type="search"
                  placeholder="Search by title, URL, or owner"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  aria-label="Search QR codes"
                />
              </div>
              <div className="list-count">
                {filtered.length} of {codes.length} {codes.length === 1 ? "code" : "codes"}
              </div>
            </div>

            {filtered.length === 0 ? (
              <div className="empty-state">
                <div className="empty-state-title">No matches</div>
                <div className="empty-state-text">
                  Nothing matches “{query}”. Try a different title, URL, or owner.
                </div>
              </div>
            ) : (
              <div className="table-scroll">
                <table>
                  <thead>
                    <tr>
                      <th>QR</th>
                      <th>Title</th>
                      <th>Owner</th>
                      <th>Destination</th>
                      <th>Created</th>
                      <th>Scans</th>
                      <th>Status</th>
                      <th className="col-actions">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((qr) => (
                      <tr key={qr.id}>
                        <td>
                          {qr.generated_image ? (
                            <img
                              className="qr-thumb"
                              src={qr.generated_image}
                              alt={qr.title}
                              onClick={() => setPreviewQr(qr)}
                            />
                          ) : (
                            <div className="qr-thumb-empty">
                              <QrIcon size={20} />
                            </div>
                          )}
                        </td>
                        <td className="cell-strong">{qr.title}</td>
                        <td className="cell-muted">{qr.owner_username || "—"}</td>
                        <td className="cell-muted cell-ellipsis" title={qr.destination_url}>
                          {qr.destination_url}
                        </td>
                        <td className="cell-muted">{fmtDateTime(qr.created_at)}</td>
                        <td className="scan-count">{qr.scan_count}</td>
                        <td>
                          <span className={`badge ${qr.is_active ? "badge-success" : "badge-muted"}`}>
                            {qr.is_active ? "Active" : "Inactive"}
                          </span>
                        </td>
                        <td>
                          <div className="row-actions">
                            {qr.generated_image && (
                              <button
                                className="icon-btn"
                                onClick={() => setPreviewQr(qr)}
                                title="Preview"
                                aria-label="Preview"
                              >
                                <EyeIcon />
                              </button>
                            )}
                            <Link to={`/edit/${qr.id}`} className="icon-btn" title="Edit" aria-label="Edit">
                              <EditIcon />
                            </Link>
                            <button
                              className={`icon-btn${qr.is_active ? " warn" : ""}`}
                              onClick={() => handleToggleActive(qr.id)}
                              disabled={togglingId === qr.id}
                              title={qr.is_active ? "Deactivate" : "Activate"}
                              aria-label={qr.is_active ? "Deactivate" : "Activate"}
                            >
                              <PowerIcon />
                            </button>
                            <button
                              className="icon-btn danger"
                              onClick={() => setConfirmQr(qr)}
                              title="Delete"
                              aria-label="Delete"
                            >
                              <TrashIcon />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>

      <PreviewModal qr={previewQr} onClose={() => setPreviewQr(null)} onDownload={handleDownload} />
      <ConfirmDeleteModal
        qr={confirmQr}
        deleting={deleting}
        onClose={() => !deleting && setConfirmQr(null)}
        onConfirm={handleConfirmDelete}
      />
    </div>
  );
}